import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Input, Button, LanguageTabs, Loading } from '../components/ui';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';

interface ContactContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  email: string;
  location_en: string;
  location_ro: string;
  artist_name: string;
  artist_alias: string;
}

const ContactPage: React.FC = () => {
  const { data, loading, fetch, update } = useApi<ContactContent>('/api/content/contact');
  const { showToast } = useToast();
  const [lang, setLang] = useState<'en' | 'ro'>('en');
  const [form, setForm] = useState<ContactContent>({
    title_en: '', title_ro: '',
    subtitle_en: '', subtitle_ro: '',
    email: '',
    location_en: '', location_ro: '',
    artist_name: '', artist_alias: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetch(); }, [fetch]);

  useEffect(() => {
    if (data) {
      setForm({
        title_en: data.title_en || '',
        title_ro: data.title_ro || '',
        subtitle_en: data.subtitle_en || '',
        subtitle_ro: data.subtitle_ro || '',
        email: data.email || '',
        location_en: data.location_en || '',
        location_ro: data.location_ro || '',
        artist_name: data.artist_name || '',
        artist_alias: data.artist_alias || '',
      });
    }
  }, [data]);

  const handleChange = (field: keyof ContactContent) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await update(form);
    setSaving(false);
    if (result) {
      showToast('Contact section saved!', 'success');
    } else {
      showToast('Failed to save changes', 'error');
    }
  };

  if (loading && !data) return <Loading />;

  return (
    <>
      <PageHeader
        title="Contact Section"
        subtitle="Edit contact information and section text"
        actions={<Button onClick={handleSave} loading={saving}>Save Changes</Button>}
      />

      <div className="admin-content">
        <Card title="Section Header">
          <LanguageTabs currentLang={lang} onChange={setLang} />
          <div className="admin-form">
            {lang === 'en' ? (
              <>
                <Input label="Title (EN)" value={form.title_en} onChange={handleChange('title_en')} placeholder="Let's Collaborate" />
                <Input label="Subtitle (EN)" value={form.subtitle_en} onChange={handleChange('subtitle_en')} placeholder="Let's make music together." />
              </>
            ) : (
              <>
                <Input label="Title (RO)" value={form.title_ro} onChange={handleChange('title_ro')} placeholder="Hai să colaborăm" />
                <Input label="Subtitle (RO)" value={form.subtitle_ro} onChange={handleChange('subtitle_ro')} placeholder="Hai să facem muzică împreună." />
              </>
            )}
          </div>
        </Card>

        <div style={{ marginTop: 24 }}>
          <Card title="Contact Information">
            <div className="admin-form">
              <Input
                label="Email Address"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="your@email.com"
              />
              <div className="admin-form-row">
                <Input
                  label="Artist Name"
                  value={form.artist_name}
                  onChange={handleChange('artist_name')}
                  placeholder="andrxw"
                />
                <Input
                  label="Artist Alias"
                  value={form.artist_alias}
                  onChange={handleChange('artist_alias')}
                  placeholder="lyandrxw"
                />
              </div>
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 24 }}>
          <Card title="Location">
            <LanguageTabs currentLang={lang} onChange={setLang} />
            <div className="admin-form">
              {lang === 'en' ? (
                <Input label="Location (EN)" value={form.location_en} onChange={handleChange('location_en')} placeholder="Romania" />
              ) : (
                <Input label="Location (RO)" value={form.location_ro} onChange={handleChange('location_ro')} placeholder="România" />
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
